import { useState } from 'react';
import API from '@/lib/api';
import { toast } from 'sonner';
import { Sparkle } from '@phosphor-icons/react';
import { hsLevelLabel } from '@/lib/hscode';

/**
 * Herbruikbaar modal voor het reviewen van een AI HS-code suggestie.
 *
 * Props:
 *  - artikel: { id, omschrijving, art_code, categorie, foto_url, oorsprong_land? }
 *  - suggestie: { hs_code, confidence, beschrijving, motivatie, alternatieven? }
 *  - gebruiktMateriaal?: string (toon-only, ingang die werd gebruikt voor de huidige suggestie)
 *  - onApply: (hsCode: string, meta?: { beschrijving?: string, motivatie?: string }) => Promise<void> | void
 *  - onClose: () => void
 *  - onSuggestieChange: (suggestie, gebruiktMateriaal) => void
 */
export default function HsCodeReviewModal({ artikel, suggestie, gebruiktMateriaal, onApply, onClose, onSuggestieChange }) {
  const [refineMateriaal, setRefineMateriaal] = useState('');
  const [refineBusy, setRefineBusy] = useState(false);

  if (!artikel || !suggestie) return null;

  const handleClose = () => {
    setRefineMateriaal('');
    onClose?.();
  };

  const refineMet = async (materiaal) => {
    if (!materiaal) return;
    setRefineBusy(true);
    try {
      const r = await API.post('/artikels/find-hscode', {
        omschrijving: artikel.omschrijving,
        categorie: artikel.categorie,
        foto_url: artikel.foto_url,
        materiaal,
      });
      if (!r.data?.ok) {
        toast.error('AI kon geen HS-code voorstellen', { description: r.data?.error });
        return;
      }
      onSuggestieChange?.(r.data, materiaal);
      toast.info(`Voorstel verfijnd met materiaal: ${materiaal}`);
    } catch (e) {
      toast.error('Fout', { description: e?.response?.data?.detail || e.message });
    } finally {
      setRefineBusy(false);
    }
  };

  const fotoSrc = artikel.foto_url
    ? (artikel.foto_url.startsWith('http')
        ? artikel.foto_url
        : `${process.env.REACT_APP_BACKEND_URL}${artikel.foto_url}`)
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(8px)' }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full overflow-hidden" data-testid="hs-review-modal">
        {/* Header met artikel info + foto */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-5 border-b border-blue-100 flex gap-4">
          {fotoSrc && (
            <img
              src={fotoSrc}
              alt=""
              className="w-20 h-20 object-cover rounded-lg border-2 border-white shadow-sm flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-blue-700 font-bold mb-1">🤖 AI HS-code Suggestie</div>
            <div className="text-base font-bold text-gray-900 truncate">{artikel.omschrijving}</div>
            <div className="text-[11px] text-gray-500 font-mono">
              {artikel.art_code || '—'}
              {artikel.categorie ? ` · ${artikel.categorie}` : ''}
              {gebruiktMateriaal ? ` · 🧪 ${gebruiktMateriaal}` : ''}
            </div>
          </div>
        </div>

        <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
          {/* Hoofdsuggestie */}
          <div className="bg-gradient-to-r from-emerald-50 to-emerald-50/50 border-2 border-emerald-300 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1">⭐ Beste match</div>
                <div className="text-2xl font-mono font-bold text-emerald-900">{suggestie.hs_code}</div>
                {hsLevelLabel(suggestie.hs_code) && (
                  <div className="text-[10px] text-emerald-700 font-bold mt-0.5">{hsLevelLabel(suggestie.hs_code)}</div>
                )}
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-emerald-700">{suggestie.confidence}%</div>
                <div className="text-[10px] text-emerald-600 uppercase">confidence</div>
              </div>
            </div>
            <div className="text-sm font-semibold text-gray-900 mb-1">{suggestie.beschrijving}</div>
            {suggestie.motivatie && (
              <div className="text-xs text-gray-600 italic mb-3">💡 {suggestie.motivatie}</div>
            )}
            <button
              type="button"
              className="w-full btn btn-primary btn-sm"
              style={{ background: 'linear-gradient(135deg,#10B981 0%,#059669 100%)', borderColor: '#059669' }}
              onClick={() => onApply?.(suggestie.hs_code, { beschrijving: suggestie.beschrijving, motivatie: suggestie.motivatie })}
              data-testid="hs-review-accept-main"
            >
              ✅ Toepassen — {suggestie.hs_code}
            </button>
          </div>

          {/* Alternatieven */}
          {(suggestie.alternatieven || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Alternatieven</div>
              <div className="space-y-2">
                {suggestie.alternatieven.map((alt, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-lg p-3 hover:border-blue-400 transition-colors">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <div className="font-mono font-bold text-gray-900">{alt.hs_code}</div>
                      <div className="text-xs font-bold text-gray-500">{alt.confidence}%</div>
                    </div>
                    <div className="text-xs text-gray-600 mb-2">{alt.beschrijving}</div>
                    <button
                      type="button"
                      className="btn btn-outline btn-xs"
                      onClick={() => onApply?.(alt.hs_code, { beschrijving: alt.beschrijving, motivatie: '' })}
                      data-testid={`hs-review-accept-alt-${i}`}
                    >
                      Kies dit
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Verfijn met materiaal/grondstof */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-lg p-3 mt-3">
            <div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold mb-1">🧪 Niet juist? Verfijn met grondstof</div>
            <div className="text-[11px] text-amber-700 mb-2 leading-relaxed">
              Geef het exacte materiaal/grondstof — bv. <span className="font-mono bg-white px-1 py-0.5 rounded">DX51D Z100</span> (verzinkt staal),
              <span className="font-mono bg-white px-1 py-0.5 rounded ml-1">PP</span> (polypropyleen),
              <span className="font-mono bg-white px-1 py-0.5 rounded ml-1">EN AW-6060</span> (aluminium legering), …
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="bv. DX51D Z100"
                className="field-input"
                style={{ flex: 1, fontSize: 13 }}
                value={refineMateriaal}
                onChange={(e) => setRefineMateriaal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && refineMateriaal.trim() && !refineBusy) {
                    refineMet(refineMateriaal.trim());
                  }
                }}
                data-testid="hs-refine-materiaal"
              />
              <button
                type="button"
                className="btn btn-primary btn-sm inline-flex items-center gap-1.5"
                style={{ background: '#D97706', borderColor: '#D97706' }}
                onClick={() => refineMet(refineMateriaal.trim())}
                disabled={!refineMateriaal.trim() || refineBusy}
                data-testid="hs-refine-zoek"
              >
                <Sparkle size={13} weight="duotone" />
                {refineBusy ? 'Zoeken…' : '🔄 Opnieuw zoeken'}
              </button>
            </div>
          </div>

          {/* Manueel invullen */}
          <div className="border-t border-gray-100 pt-3">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Of vul handmatig in</div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="bv. 73089051"
                className="field-input font-mono"
                style={{ flex: 1 }}
                maxLength={10}
                data-testid="hs-review-manual-input"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.target.value.length >= 4) {
                    onApply?.(e.target.value.replace(/[^0-9]/g, ''), { beschrijving: '', motivatie: '' });
                  }
                }}
              />
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={(e) => {
                  const inp = e.target.parentElement.querySelector('input');
                  if (inp.value.length >= 4) onApply?.(inp.value.replace(/[^0-9]/g, ''), { beschrijving: '', motivatie: '' });
                }}
              >
                Toepassen
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-5 py-3 border-t border-gray-200 flex justify-between items-center">
          <button
            type="button"
            className="text-sm text-gray-500 hover:text-gray-700"
            onClick={handleClose}
            data-testid="hs-review-cancel"
          >
            Annuleren
          </button>
          <div className="text-[10px] text-gray-400">⚠️ Adviserend — bevestig zelf vóór douaneaangifte</div>
        </div>
      </div>
    </div>
  );
}
